import React, {useEffect, useState} from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  FormGroup,
  InputLabel,
  Link,
  MenuItem,
  Typography,
  TextField,
  useTheme,
  Skeleton
} from '@mui/material';
import { useNavigate } from "react-router-dom";
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { SquarePen, ChevronDown, X } from 'lucide-react-new';
import { Plus as PlusIcon } from 'lucide-react';
import useAuth from 'src/hooks/useAuth';
import { hashrateUnitOptions, convertToGHValue } from 'src/utils/alerts';
import CreateNewSubAccountAlert from 'src/api/CreateNewSubAccountAlert';
import EmailChipsInput from 'src/components/EmailChipsInput.js'
import GetGrantedSubAccounts from 'src/api/GetGrantedSubAccounts';
import { applySorting, getComparator } from '../utils/filterAndSortData';

const useStyles = makeStyles()(theme => ({
  metalRoot: {
    backgroundColor: 'var(--neutral-1)',
    minHeight: '100%',
    [theme.breakpoints.up('md')]: {
      padding: `0 ${theme.spacing(4)} ${theme.spacing(0.5)}`
    }
  },
  selectLabel: {
    color: "var(--colors-text-text-secondary-700, #CDD5DF)",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
    marginTop: "8px",
    marginBottom: "8px"
  },
  selectField: {
    borderRadius: "var(--radius-md, 8px)",
    border: "1px solid var(--Colors-Border-border-primary, #364152)",
    background: "var(--Colors-Background-bg-primary, #0D121C)",
    boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
    marginBottom: "8px"
  },
  splitFieldLeft: {
    width: "75%",
    borderTopLeftRadius: "var(--radius-md, 8px)",
    borderBottomLeftRadius: "var(--radius-md, 8px)",
    border: "1px solid var(--Colors-Border-border-primary, #364152)",
    borderRight: "none",
    background: "var(--Colors-Background-bg-primary, #0D121C)",
    boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
    marginBottom: "8px",
    '& .MuiOutlinedInput-notchedOutline':{

      borderTopRightRadius: 0,
      borderBottomRightRadius: 0,
    }
  },
  splitFieldRight:{
    width: "25%",
    borderTopRightRadius: "var(--radius-md, 8px)",
    borderBottomRightRadius: "var(--radius-md, 8px)",
    border: "1px solid var(--Colors-Border-border-primary, #364152)",
    borderLeft: "none",
    background: "var(--Colors-Background-bg-primary, #0D121C)",
    boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
    marginBottom: "8px",
    '& .MuiOutlinedInput-notchedOutline':{

      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0,
    }
  },
  buttonBase: {
    display: "flex",
    padding: "10px var(--spacing-xl, 16px)",
    justifyContent: "center",
    alignItems: "center",
    gap: "var(--spacing-sm, 6px)",
    flex: "1 0 0",
    borderRadius: "var(--radius-md, 8px)",
    border: "1px solid var(--Component-colors-Components-Buttons-Secondary-button-secondary-border, #333741)",
    background: "var(--Component-colors-Components-Buttons-Secondary-button-secondary-bg, #161B26)",
    boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
    color: "#CECFD2",
    textTransform: "capitalize",
    fontSize: "16px",
    fontWeight: "600",
    lineHeight: "24px"
  },
  buttonAction: {
    padding: "7px 15px",
    borderRadius: "var(--radius-md, 8px)",
    backgroundColor: "var(--accent-9)",
    color: "var(--accent-9-contrast)",
    '&:hover, &.Mui-focusVisible': {
      backgroundColor: 'var(--accent-9)',
    },
    '& + &': {
      marginLeft: theme.spacing(1)
    },
    marginLeft: "10px",
  },
  actionIcon: {
    marginRight: theme.spacing(1)
  },
  snackbarLink: {
    position: "relative",
    left: "-8px",
    color: "#fff",
    cursor: "pointer"
  },
}));

const CreateAlertButton = (props) => {
    const navigate = useNavigate(); 
    const { enqueueSnackbar } = useSnackbar();
    const { loggedInUser, getAccessToken }  = useAuth();
    const { t } = useTranslation();
    const theme = useTheme();
    const { classes } = useStyles();
    const {coin, update, setUpdate} = props;
    const defaultGroups = loggedInUser.groups.flatMap(i => i.group)
    const [loading, setLoading] = useState(true)
    const [ subAccountAndGroupNames, setSubAccountAndGroupNames ] = useState([])
    const [ createAlertDialog, setCreateAlertDialog ] = useState(false)
    const [ selectedGroup, setSelectedGroup ] = useState(defaultGroups[0].name)
    const [ selectedSubAccount, setSelectedSubAccount ] = useState('')
    const [ subAccounts, setSubAccounts ] = useState('')
    const [ hashrateUnit, setHashrateUnit ] = useState(hashrateUnitOptions[1].value)
    const [ activeWorkersThreshold, setActiveWorkersThreshold ] = useState('')
    const [ hashrateThreshold, setHashrateThreshold ] = useState('')
    const [ emailsTextArea, setEmailsTextArea ] = useState('')
    const [ newAlertContainerLoading, setNewAlertContainerLoading ] = useState(false)
    const [ emailList, setEmailList ] = useState([])
    const [ emailListError, setEmailListError ] = useState(false)

    const handleNavigate = () => {
        navigate("/app/alerts/manage")
    }

    const handleCreateAlert = () => {
        setCreateAlertDialog(true);
    };

    const closeCreateAlertDialog = () => {
        setCreateAlertDialog(false);
        setSelectedGroup(defaultGroups[0].name)
        setSelectedSubAccount(subAccountAndGroupNames.filter(i => i.groupName === selectedGroup)[0].subAccountName)
        setHashrateUnit(hashrateUnitOptions[1].value)
        setActiveWorkersThreshold('')
        setHashrateThreshold('')
        setEmailsTextArea('')
        setEmailList([])
    };

    const handleGroupChange = (e) => {
    setSelectedGroup(e.target.value)
    }

    const handleSubAccountChange = (e) => {
    setSelectedSubAccount(e.target.value)
    }

    const handleHashrateUnitChange = (e) => {
    setHashrateUnit(e.target.value)
    } 

    const handleActiveWorkersThreshold = (e) => {
    setActiveWorkersThreshold(e.target.value)
    }

    const handleHashrateThreshold = (e) => {
    setHashrateThreshold(e.target.value)
    }

    function handleSelectedEmails(emails) {
    setEmailList(emails);
    }

    const handleSaveNewAlert = async () => {    
        //convert hashrate threshold value to gh/s 
        var convertedGHValue = convertToGHValue(hashrateUnit, hashrateThreshold.replaceAll(",", ""))
        setNewAlertContainerLoading(true)
        const {tokenAvailable, accessToken} = await getAccessToken()
        
        if (!tokenAvailable) {
        return;
        }
        
        const {success, data} = await CreateNewSubAccountAlert(coin, selectedSubAccount, convertedGHValue, activeWorkersThreshold.replaceAll(",", ""), emailList, accessToken);
        setNewAlertContainerLoading(false)
        if (success){
            enqueueSnackbar(`${t('alertCreatedSuccessfully')}.`
            ,   {
                variant: 'success',
                action: window.location.pathname === '/app/alerts/active' 
                    ?   <Link className={classes.snackbarLink} onClick={handleNavigate}>
                            View alert here
                        </Link>
                    :   null
                }
            )
            if(typeof update !== "undefined") {
                setUpdate(!update)
            }
            closeCreateAlertDialog()
        }
        else{
        enqueueSnackbar(data,{
            variant: 'error'
        })
        }
    } 

    
    const handleGetSubAccounts = async () => {
        const { tokenAvailable, accessToken } = await getAccessToken();

        if (!tokenAvailable) {
            return;
        }

        const data = await GetGrantedSubAccounts(accessToken)
    
        if (data.success) {
            // Flattens and sorts the user's sub-accounts and roles
            const sortedData = data.data.flatMap(item =>
                item.subAccountNamesAndRoles.map(sa => ({
                    groupName: item.groupName,
                    subAccountName: sa.name
                }))
            );

            setSubAccountAndGroupNames(sortedData);
        } else {
            enqueueSnackbar(t('failedToRetrieveSubAccounts'), {
                variant: 'error'
            });
        }
    }

    useEffect(()=>{
        setLoading(true)
        handleGetSubAccounts()
        setLoading(false);
    }, [])

    useEffect(()=>{
    if(subAccountAndGroupNames.length && subAccountAndGroupNames.filter(i => i.groupName === selectedGroup).length){
        const unsortedSubAccounts = subAccountAndGroupNames.filter(i => i.groupName === selectedGroup)
        const sortedSubAccounts = applySorting(unsortedSubAccounts, getComparator('asc', 'subAccountName'))
        setSubAccounts(sortedSubAccounts)
        setSelectedSubAccount(sortedSubAccounts.filter(i => i.groupName === selectedGroup)[0].subAccountName)
    }
    }, [selectedGroup, subAccountAndGroupNames, loading])

    return (
        <>
            <Button 
                style={props.buttonStyle}
                variant="outline"
                onClick={handleCreateAlert}
                startIcon={<PlusIcon size={26}/>}
                className={classes.buttonAction}>
                {t('createAlert')}      
            </Button>
            <Dialog
                PaperProps={{ sx: { backgroundColor: '#0D121C', borderRadius: "12px"} }}
                open={createAlertDialog}
                onClose={closeCreateAlertDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle component={'div'} style={{display: "flex", flexDirection: "column"}}>
                    <Box>
                        <div style={{ position: "relative", display: "flex", justifyContent:"space-between", alignItems: "center"}}>
                            <div 
                                style={{
                                marginTop: 8,
                                width: 48, 
                                height: 48, 
                                border: '1px solid #364152', 
                                borderRadius: 'var(--radius-l)',
                                display: "flex", 
                                justifyContent: "center",
                                alignItems: "center"
                                }}>
                                <SquarePen color={"#CDD5DF"}/>
                                
                            </div>
                            <div style={{
                                display: newAlertContainerLoading ? "none" : "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                marginTop: "8px", 
                                position: "relative", 
                                zIndex: 3, 
                                cursor: "pointer"
                            }} onClick={closeCreateAlertDialog}>
                                <X size={30}/>
                            </div>
                        </div>
                        <div 
                            style={{
                                position: "absolute", 
                                top: 0,
                                left: 0
                            }}>
                            <svg width="240" height="240" viewBox="0 0 240 240" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="48" cy="48" r="48" stroke="#202939"/>
                                <circle cx="48" cy="48" r="80" stroke="#202939"/>
                                <circle cx="48" cy="48" r="112" stroke="#202939" opacity={0.6}/>
                                <circle cx="48" cy="48" r="144" stroke="#202939" opacity={0.4} />
                                <circle cx="48" cy="48" r="176" stroke="#202939" opacity={0.2} />
                            </svg>
                        </div>
                        <Typography style={{marginTop: "24px", fontSize: "18px", fontWeight: "600", lineHeight: "28px", position: "relative"}}>{t('newAlert')}</Typography>
                        <Typography style={{fontSize: "14px", fontWeight: "400", color: "#94969C", position: "relative"}}>{t('newAlertSupportText')}</Typography>
                    </Box>
                </DialogTitle>
                <DialogContent
                    style={{
                        backgroundColor: '#0D121C',
                        paddingLeft: theme.spacing(3),
                        paddingRight: theme.spacing(3),
                        paddingBottom: 0
                    }}
                    > 
                    <FormGroup>
                        <InputLabel className={classes.selectLabel}>{t('group')}</InputLabel>
                        <TextField
                            disabled={newAlertContainerLoading}
                            size="large"           
                            onChange={handleGroupChange}
                            fullWidth
                            select
                            SelectProps={{
                                IconComponent: (props) => <ChevronDown style={{top: "unset"}} {...props} />,
                                native: false,
                                MenuProps:{
                                PopoverClasses: {
                                    paper: classes.tagDropDown,
                                    classes: {icon : {top: "0"}}
                                }
                                }
                            }}
                            value={selectedGroup}
                            variant="outlined"
                            className={classes.selectField}
                        >
                            {loggedInUser.groups.flatMap(i => i.group).map(j => j.name).sort().map((option) => (
                                <MenuItem
                                key={option}
                                value={option}
                                >
                                {t(option)}
                                </MenuItem>
                            ))}
                        </TextField>
                    </FormGroup>
                    <FormGroup>
                        <InputLabel className={classes.selectLabel}>{t('subAccount')}</InputLabel>
                        <TextField
                            disabled={newAlertContainerLoading || !subAccounts.length}
                            size="large"           
                            onChange={handleSubAccountChange}
                            fullWidth
                            select={subAccounts.length}
                            SelectProps={{
                                IconComponent: (props) => <ChevronDown style={{top: "unset"}} {...props} />,
                                native: false,
                                MenuProps:{
                                PopoverClasses: {
                                    paper: classes.tagDropDown,
                                    classes: {icon : {top: "0"}}
                                }
                                }
                            }}
                            value={subAccounts.length ? selectedSubAccount : "Loading..."}
                            variant="outlined"
                            className={classes.selectField}
                        >
                            {subAccounts.length && subAccounts.map((option) => (
                                <MenuItem
                                    key={option.uniqueId}
                                    value={option.subAccountName}
                                >
                                    {t(option.subAccountName)}
                                </MenuItem>
                            ))}
                        </TextField>
                    </FormGroup>
                    <FormGroup>
                        <InputLabel className={classes.selectLabel}>{t('hashrateThreshold')}</InputLabel>
                        <Box style={{display: "flex"}}>
                        <TextField 
                            onChange={handleHashrateThreshold} 
                            value={hashrateThreshold}
                            className={classes.splitFieldLeft}/>
                        <TextField
                            disabled={newAlertContainerLoading}
                            size="large"           
                            onChange={handleHashrateUnitChange}
                            select
                            SelectProps={{
                            IconComponent: (props) => <ChevronDown style={{top: "unset"}} {...props} />,
                            native: false,
                            MenuProps:{
                                PopoverClasses: {
                                paper: classes.tagDropDown,
                                classes: {icon : {top: "0"}}
                                }
                            }
                            }}
                            value={hashrateUnit}
                            className={classes.splitFieldRight}
                            >
                            {hashrateUnitOptions.map((option) => (
                                <MenuItem
                                key={option.uniqueId}
                                value={option.value}
                                >
                                {t(option.label)}
                                </MenuItem>
                            ))}
                        </TextField>
                        </Box>
                    </FormGroup>
                    <FormGroup>
                        <InputLabel className={classes.selectLabel}>{t('activeWorkersThreshold')}</InputLabel>
                        <TextField
                            disabled={newAlertContainerLoading}
                            size="large"           
                            onChange={handleActiveWorkersThreshold}
                            fullWidth
                            SelectProps={{
                                IconComponent: (props) => <ChevronDown style={{top: "unset"}} {...props} />,
                                native: false,
                                MenuProps:{
                                PopoverClasses: {
                                    paper: classes.tagDropDown,
                                    classes: {icon : {top: "0"}}
                                }
                                }
                            }}
                            value={activeWorkersThreshold}
                            variant="outlined"
                            className={classes.selectField}
                        />
                    </FormGroup>
                    <FormGroup>
                        <InputLabel className={classes.selectLabel}>{t('emailListAlertNotification')}</InputLabel>
                        <EmailChipsInput 
                            handleSelectedEmails={handleSelectedEmails}
                            fullWidth
                            disabled={newAlertContainerLoading}
                            value={emailsTextArea}
                            setEmailListError={setEmailListError}
                        />
                    </FormGroup>
                </DialogContent>
                <DialogActions
                    style={{
                        display: "flex",
                        justifyContent:"center",
                        marginTop: theme.spacing(4),
                        marginBottom: theme.spacing(3),
                        paddingLeft: theme.spacing(3),
                        paddingRight: theme.spacing(3)
                    }}
                    >
                    <Button
                        disabled={newAlertContainerLoading}
                        variant="outlined"
                        onClick={closeCreateAlertDialog}
                        className={classes.buttonBase}
                    >
                        {newAlertContainerLoading ? <Skeleton width={100}/> : t('cancel')}
                    </Button>
                    <Button
                        disabled={newAlertContainerLoading || emailListError}
                        variant="outlined"
                        onClick={handleSaveNewAlert}
                        className={classes.buttonBase}
                        style={newAlertContainerLoading || emailListError
                        ? 
                            {
                            border: "1px solid var(--Component-colors-Components-Buttons-Primary-button-primary-border, #00C067)",
                            background: "var(--Component-colors-Components-Buttons-Primary-button-primary-bg, #00C067)",
                            boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
                            color: "#FFF",
                            opacity: 0.2
                            }
                        : {
                            border: "1px solid var(--Component-colors-Components-Buttons-Primary-button-primary-border, #00C067)",
                            background: "var(--Component-colors-Components-Buttons-Primary-button-primary-bg, #00C067)",
                            boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
                            color: "#FFF"
                            }
                        }
                    >
                        {newAlertContainerLoading ? <Skeleton style={{ backgroundColor: "#FFF"}} width={100}/> : t('save')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default CreateAlertButton